"use client";

import Image from "next/image";
import { memo } from "react";
import { TVOverlay } from "@/components/tv-overlay";
import { ARCADE_STYLES, HIBERNATION_POKU_FILTER_STRONG, KANSEI_DEAD } from "@/constants/arcade-styles";
import { useArcadeData } from "@/hooks/arcade";
import { formatCompactNumber } from "@/utils";

interface StatCardProps {
  value: string | number;
  label: string;
  icon: string;
  iconAlt: string;
  isLoading: boolean;
  colorClass: string;
  iconSize?: number;
  roundedClass?: string;
  flickerVariant?: "a" | "b" | "c";
}

const STAT_CARD_STYLE = {
  ...ARCADE_STYLES.STATS_CARD.base,
  // Layer scanline residue ON TOP of the STATS_CARD gradient.
  // Spreading STATS_CARD.base then overriding `backgroundImage`
  // with just the scanline would replace the gradient and the
  // card would read transparent.
  backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, linear-gradient(135deg, #0a0a0b 0%, #141416 100%)`,
  transition: `${KANSEI_DEAD.TRANSITION.BORDER_QUANTUM}, ${KANSEI_DEAD.TRANSITION.SHADOW_QUANTUM}`,
};

const ICON_GHOST_BOX_STYLE = {
  width: 24,
  height: 24,
  color: KANSEI_DEAD.ICON_GHOST_COLOR,
};

// Memoized stat card component — KANSEI dead-lighting register.
//
// All chromatic CRT signal (green RGB-shift halos, breathing pulses,
// ease-in-out scale tweens) stripped. The card reads as a cooled-CRT
// telemetry tile: scanline atmosphere on top of the moondust surface,
// stepped border-color hover (steps(4)), icon as outline ghost, value
// as pure moondust mono.
const StatCard = memo(
  ({
    value,
    label,
    icon,
    iconAlt,
    isLoading,
    colorClass,
    iconSize = 24,
    roundedClass = "",
    flickerVariant = "a",
  }: StatCardProps) => (
    <div
      className={`stat-card-kansei relative overflow-hidden h-16 flex items-center p-3 ${roundedClass}`}
      style={STAT_CARD_STYLE}
    >
      <TVOverlay intensity="light" />
      <div className="relative z-10 flex items-center w-full gap-2">
        {/* Icon — outline ghost. No float, no chromatic halo, no breathing. */}
        <div
          className="relative shrink-0 flex items-center justify-center"
          style={ICON_GHOST_BOX_STYLE}
        >
          <Image
            src={icon}
            alt={iconAlt}
            width={iconSize}
            height={iconSize}
            style={{
              width: iconSize,
              height: iconSize,
              filter: HIBERNATION_POKU_FILTER_STRONG,
              opacity: 0.55,
            }}
            className={`object-contain kansei-flicker-${flickerVariant}`}
          />
        </div>
        <div className="flex flex-col">
          <span className={`${colorClass} font-pressStart2P text-xs`}>
            {isLoading ? "..." : value}
          </span>
          <p className="font-pressStart2P text-[5px] uppercase text-muted-foreground">{label}</p>
        </div>
      </div>
      <style jsx>{`
        .stat-card-kansei:hover {
          border-color: rgba(94, 123, 250, 0.35) !important;
        }
      `}</style>
    </div>
  ),
);

StatCard.displayName = "StatCard";

// Main stats section component
export const StatsSection = memo(() => {
  const {
    supplyLocked,
    isLoadingSupply,
    displayBurned,
    isLoadingBurn,
    holderStats,
    isLoadingHolders,
  } = useArcadeData();

  const formatHolderCount = (count?: number | null) => {
    if (typeof count !== "number" || Number.isNaN(count)) return "0";
    if (count >= 1_000_000) {
      return formatCompactNumber(count);
    }
    return count.toLocaleString();
  };

  return (
    <div>
      <div className="relative overflow-hidden rounded-t-md" style={ARCADE_STYLES.SECTION_HEADER}>
        <div className="px-3 py-1">
          {/* Stats header — moondust zinc + dying flicker. Matches the
              Play / Create / Henlo TV section labels for register
              coherence: the lobby is closed, the section labels are
              ambient chrome, the live data sits inside. */}
          <h2 className="kansei-flicker-b font-pressStart2P text-[8px] uppercase tracking-[0.18em] text-zinc-500">
            Stats
          </h2>
        </div>
      </div>
      <div className="relative rounded-b-md p-2" style={ARCADE_STYLES.SECTION_BODY}>
        <div className="flex flex-col gap-1">
          <StatCard
            value={isLoadingSupply ? "..." : (supplyLocked ?? "—")}
            label="Locked"
            icon="/lobby/icons/locked.svg"
            iconAlt="Locked"
            isLoading={isLoadingSupply}
            colorClass="text-zinc-400"
            roundedClass="rounded-t-md"
            flickerVariant="a"
          />
          <StatCard
            value={isLoadingBurn ? "..." : displayBurned}
            label="Burned"
            icon="/lobby/icons/burned.svg"
            iconAlt="Burned"
            isLoading={isLoadingBurn}
            colorClass="text-zinc-400"
            flickerVariant="b"
          />
          <StatCard
            value={
              isLoadingHolders
                ? "..."
                : formatHolderCount(holderStats?.uniqueHolders ?? 0)
            }
            label="Holders"
            icon="/lobby/icons/holders.svg"
            iconAlt="Holders"
            isLoading={isLoadingHolders}
            colorClass="text-zinc-400"
            iconSize={16}
            roundedClass="rounded-b-md"
            flickerVariant="c"
          />
        </div>
      </div>
    </div>
  );
});

StatsSection.displayName = "StatsSection";
